/**
 * Browser port of Rest.Api.Utils.DynamicExpiringPresharedKey (decrypt path).
 *
 * The IoT device's User.Name / User.Password are AES-GCM ciphertexts produced
 * with a short-lived preshared-key "package" (GET /api/iot/v1/presharedkey).
 * Decrypt them here to obtain the real MQTT username / password.
 *
 * Package layout (base64url):  header(24) | sessionKey(32) | mac(32)
 * Message layout (base64url):  header(24) | nonce(12) | ciphertext | tag(16)
 * AES-GCM: key = sessionKey, iv = nonce, additionalData = header, tag = 128 bit.
 */

const HEADER_LEN = 24; // expiry(8) + salt(16)
const NONCE_LEN = 12;
const TAG_LEN = 16;
const SESSION_KEY_LEN = 32;

export const fromBase64Url = (s: string): Uint8Array => {
 let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
 const mod = b64.length % 4;
 if (mod === 2) b64 += '==';
 else if (mod === 3) b64 += '=';
 else if (mod !== 0) throw new Error('Invalid Base64Url length.');
 const bin = atob(b64);
 const out = new Uint8Array(bin.length);
 for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
 return out;
};

/**
 * Decrypt a base64url message with a base64url preshared-key package.
 * Mirrors DecryptWithPackage (AAD = package header).
 */
export const decryptWithPresharedKey = async (
 presharedKeyB64: string,
 messageB64: string,
): Promise<string> => {
 const pkg = fromBase64Url(presharedKeyB64);
 if (pkg.length < HEADER_LEN + SESSION_KEY_LEN) {
  throw new Error('Invalid preshared key package length.');
 }
 const header = pkg.slice(0, HEADER_LEN);
 const sessionKey = pkg.slice(HEADER_LEN, HEADER_LEN + SESSION_KEY_LEN);

 const msg = fromBase64Url(messageB64);
 if (msg.length < HEADER_LEN + NONCE_LEN + TAG_LEN) {
  throw new Error('Message too short.');
 }
 const nonce = msg.slice(HEADER_LEN, HEADER_LEN + NONCE_LEN);
 // WebCrypto expects ciphertext || tag concatenated → everything after nonce.
 const cipherAndTag = msg.slice(HEADER_LEN + NONCE_LEN);

 const key = await crypto.subtle.importKey(
  'raw',
  sessionKey,
  { name: 'AES-GCM' },
  false,
  ['decrypt'],
 );

 const plaintext = await crypto.subtle.decrypt(
  { name: 'AES-GCM', iv: nonce, additionalData: header, tagLength: 128 },
  key,
  cipherAndTag,
 );

 return new TextDecoder().decode(plaintext);
};

export default decryptWithPresharedKey;
